import { useEffect, useRef } from "react";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { useUiAudioStateContext } from "hooks/useUiAudioStateContext";

/** Реплика так и не появилась (например, «+» в паре не от русского) — просьба не ждёт вечно. */
const REQUEST_TTL_MS = 15000;
/** Лента доезжает плавно; поле ввода получает фокус, когда оно уже на экране. */
const FOCUS_DELAY_MS = 400;

/**
 * Исполняет просьбу провайдера `focusCue` (новая реплика «+»): как только реплика появилась на
 * дорожке, лента едет к ней, строка выделяется, а курсор встаёт в поле перевода (или оригинала,
 * если пары нет на экране). Раньше после «+» не происходило ничего видимого, и переводчики
 * нажимали его ещё раз.
 */
export const useFocusRequestedCue = ({ zoomLevel, onlyRuLang }: { zoomLevel: number; onlyRuLang: boolean }) => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const uiState = useUiAudioStateContext();
    const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    useEffect(
        () => () => {
            timersRef.current.forEach(clearTimeout);
        },
        [],
    );

    useEffect(() => {
        const request = state.focusCue;
        if (!request) return;
        // Номер папки — числом: «К реплике» отчёта правки эфира несёт номер из полосы, а папка редактора бывает строкой.
        if (Number(request.folder) !== Number(state.folder) || Date.now() - request.at > REQUEST_TTL_MS) {
            handles.clearFocusCue();
            return;
        }
        const track = (state.translationTrackAll[state.originalLang] || []).find(
            (one) => one.original_id === request.id,
        );
        if (!track) return; // папка ещё не перечитана
        handles.clearFocusCue();
        handles.setScrollTo(Math.max(0, track.start / zoomLevel - 200));
        handles.setChangedRecently([track.id]);
        uiState.setActiveCaptionTrack(track.id);
        const side = onlyRuLang ? "source" : "translation";
        // Таймеры не снимаются при смене зависимостей: снятие просьбы само меняет состояние,
        // и очистка эффекта отменила бы фокус, ради которого всё делается.
        timersRef.current.push(
            setTimeout(() => {
                const box = document.querySelector<HTMLElement>(
                    `[data-cue-id="${track.id}"][data-caption-side="${side}"] [contenteditable="true"]`,
                );
                box?.focus();
            }, FOCUS_DELAY_MS),
            setTimeout(() => handles.setChangedRecently([]), 2500),
        );
    }, [state.focusCue, state.translationTrackAll, state.originalLang, state.folder, handles, uiState, zoomLevel, onlyRuLang]);
};
